import { useState } from 'react';
import styled from 'styled-components';

import { colors } from 'styles/theme/colors';
import { BodySmallBold } from 'styles/typography';

import { LinkButton, StcTable } from './BenefitsTable';
import {
  benefitCoverageText,
  benefitDescriptionText,
  BenefitLine,
  benefitPriorAuthText,
  benefitServiceLimitText,
  partitionStcGroups,
  StcGroup,
  Stedi271,
} from './eligibilityData';

// The benefits table for a completed check: raw 271 benefit lines grouped by service-type code.
// Skilled nursing (AG/AH) is pulled into its own section, and — since a real 271 can carry 30+
// STCs — only the priority coverages (Health Benefit Plan, Medical) show expanded; the rest
// collapse behind a toggle.

interface Props {
  stedi: Stedi271;
  planName?: string;
}

// The "Benefit" cell: like Stedi's PDF, service limit + prior authorization + any messages stack
// here (the amount stays in the Coverage cell). Renders "—" only when a line carries none of them.
function BenefitCell({ line }: { line: BenefitLine }) {
  const serviceLimit = benefitServiceLimitText(line);
  const priorAuth = benefitPriorAuthText(line);
  const description = benefitDescriptionText(line);
  const message = description === '—' ? null : description;

  if (!serviceLimit && !priorAuth && !message) return <>—</>;

  return (
    <DetailStack>
      {serviceLimit && (
        <span>
          <DetailLabel>Service limit:</DetailLabel> {serviceLimit}
        </span>
      )}
      {priorAuth && (
        <span>
          <DetailLabel>Prior authorization:</DetailLabel> {priorAuth}
        </span>
      )}
      {message && <span>{message}</span>}
    </DetailStack>
  );
}

function LegacyStcTable({ group }: { group: StcGroup }) {
  return (
    <StcTable
      title={`STC ${group.stcCode}${group.stcName ? ` ${group.stcName}` : ''}`}
      rows={group.lines.map((line) => [
        line.name || '—',
        line.coverageLevel || '—',
        line.inPlanNetworkIndicator || '—',
        benefitCoverageText(line),
        <BenefitCell key='benefit' line={line} />,
      ])}
    />
  );
}

export default function BenefitsByStc({ stedi, planName }: Props) {
  const [showAll, setShowAll] = useState(false);
  const { priority, rest } = partitionStcGroups(stedi.benefitsInformation);
  if (priority.length === 0 && rest.length === 0) return null;

  return (
    <Section data-testid='eligibility-benefits'>
      <SectionHeader>
        <span>Benefits{planName ? ` – ${planName}` : ''}</span>
      </SectionHeader>

      {priority.map((group) => (
        <LegacyStcTable key={group.stcCode} group={group} />
      ))}

      {rest.length > 0 && (
        <>
          {showAll && rest.map((group) => <LegacyStcTable key={group.stcCode} group={group} />)}
          <ToggleRow>
            <LinkButton type='button' onClick={() => setShowAll((v) => !v)} data-testid='toggle-all-benefits'>
              {showAll ? 'Show fewer benefits' : `Show all benefits (${rest.length} more)`}
            </LinkButton>
          </ToggleRow>
        </>
      )}
    </Section>
  );
}

const Section = styled.div`
  border: 1px solid ${colors.black10};
  border-radius: 8px;
  overflow: hidden;
`;

const SectionHeader = styled(BodySmallBold)`
  display: flex;
  justify-content: space-between;
  padding: 16px 24px;
  font-size: 15px;
  background: ${colors.blue5};
  border-bottom: 1px solid ${colors.black10};
  color: ${colors.black100};
`;

const DetailStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailLabel = styled.span`
  color: ${colors.black50};
`;

const ToggleRow = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${colors.black10};
`;
